import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Logo from "@/components/Logo";
import ScanResults from "@/components/ScanResults";
import { useScanProgress } from "@/hooks/useScanProgress";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import ProjectDisclaimer from "@/components/ProjectDisclaimer";

import type { ScanResult } from "@/lib/api/ai-visibility";
import type { PlatformVisibilityResult } from "@/lib/api/platform-visibility";
import type { GoogleSearchResult } from "@/lib/api/google-search-visibility";

interface ResultsState {
  scanResult?: ScanResult;
  platformResult?: PlatformVisibilityResult | null;
  googleResult?: GoogleSearchResult | null;
  websiteUrl?: string;
  brandName?: string;
  keyword?: string;
  scanId?: string;
}

const ResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const state = location.state as ResultsState | null;

  const toastKey = state?.scanResult
    ? `${state.scanResult.brandName}_${state.scanResult.targetKeyword}`
    : "";
  const [stableToastKey] = useState(toastKey);

  const brandName = state?.scanResult?.brandName || state?.brandName || "";
  const keyword = state?.scanResult?.targetKeyword || state?.keyword || "";

  const scanState = useScanProgress(brandName, keyword, {
    aiResult: state?.scanResult || null,
    platformResult: state?.platformResult || null,
    googleResult: state?.googleResult || null,
  });

  useEffect(() => {
    if (!scanState.isComplete || !stableToastKey) return;
    const toastStorageKey = `toast_shown_${stableToastKey}`;
    if (!sessionStorage.getItem(toastStorageKey)) {
      sessionStorage.setItem(toastStorageKey, 'true');
      setTimeout(() => {
        toast({
          title: "Scan Complete!",
          description: "View your results below",
        });
      }, 500);
    }
  }, [scanState.isComplete, stableToastKey, toast]);

  // Update URL to shareable link after scan completes
  const scanId = state?.scanResult?.scan_id || state?.scanId;
  const [urlUpdated, setUrlUpdated] = useState(false);

  useEffect(() => {
    if (scanId && !urlUpdated) {
      window.history.replaceState(null, '', `/scan/${scanId}`);
      setUrlUpdated(true);
    }
  }, [scanId, urlUpdated]);

  useEffect(() => {
    if (!state?.scanResult && !brandName) {
      navigate('/', { replace: true });
    }
  }, [state, brandName, navigate]);

  if (!brandName) {
    return (
      <main className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </main>
    );
  }

  const aiResult = scanState.aiResult || state?.scanResult;
  const platformResult = scanState.platformResult || state?.platformResult;
  const googleResult = scanState.googleResult || state?.googleResult;

  const analysis = aiResult?.analysis || {
    visibilityScore: 0,
    keyFindings: [],
    competitorMentions: [],
    recommendations: [],
    summary: "",
  };

  return (
    <>
      <Helmet>
        <title>{brandName ? `${brandName} AI Visibility Results | AILens` : 'Scan Results | AILens'}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <main className="min-h-screen bg-background bg-dot-grid flex flex-col items-center px-4 py-0 pb-12 relative overflow-hidden">
        {/* Sticky results header */}
        <header className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur-sm border-b border-border/40 shadow-sm mb-6">
          <div className="max-w-5xl mx-auto flex items-center justify-between px-4 py-3">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              New Scan
            </Link>
            <a href="/" className="hover:opacity-80 transition-opacity">
              <Logo />
            </a>
            {scanId ? (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(window.location.href);
                    toast({ title: "Link copied!", description: "Share this link with anyone" });
                  } catch {
                    const input = document.createElement('input');
                    input.value = window.location.href;
                    document.body.appendChild(input);
                    input.select();
                    document.execCommand('copy');
                    document.body.removeChild(input);
                    toast({ title: "Link copied!" });
                  }
                }}
              >
                <Share2 className="w-4 h-4" />
                Share
              </Button>
            ) : (
              <div className="w-[72px]" />
            )}
          </div>
        </header>

        <div className="relative z-10 flex flex-col items-center gap-6 w-full">
          {/* Results */}
          <ScanResults 
            analysis={analysis} 
            brandName={brandName}
            keyword={keyword}
            platformResults={platformResult?.platforms}
            googleResult={googleResult}
            websiteUrl={state?.websiteUrl}
            chatGptStatus={scanState.aiStatus === "idle" ? "success" : scanState.aiStatus}
            googleStatus={scanState.googleStatus === "idle" ? "success" : scanState.googleStatus}
            isFromCache={scanState.isFromCache}
            aiScore={aiResult?.aiScore}
            queryType={aiResult?.queryType}
            queryUsed={aiResult?.queryUsed}
            rawResponseSnippet={aiResult?.rawResponseSnippet}
            competitorMentions={aiResult?.competitorMentions}
          />

          {/* Footer text */}
          <p className="text-muted-foreground text-sm text-center animate-fade-in mt-8" style={{ animationDelay: "0.5s" }}>
            AILens © 2026 ·{" "}
            <a href="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</a>
            {" "}·{" "}
            <a href="/terms" className="hover:text-foreground transition-colors">Terms of Service</a>
          </p>
          <ProjectDisclaimer />
        </div>
      </main>
    </>
  );
};

export default ResultsPage;
